import {
  ForwardedRef,
  forwardRef,
  Key,
  Ref,
  RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { ListItem } from '@xemplo/listbox';

import { BaseTextDropdownMulti } from './base-text-dropdown-multi';
import {
  isEqual,
  isFieldValueEqual,
  reactAriafyValue,
} from './text-dropdown-multi.helper';
import {
  SelectRefCallback,
  TextDropdownMultiRhfProps,
  TKey,
} from './text-dropdown-multi-v2.types';

/**
 * TextDropdownMultiRhf is a wrapper around the BaseTextDropdownMulti component
 * that integrates with react-hook-form. It provides a way to use the dropdown
 * multi component with form validation and state management.
 *
 * NOTE: If you need to set default values (e.g.: edit form), you must:
 * 1. Use the `defaultValues` prop in the `useForm` hook.
 * 2. Use the `defaultSelectedKeys` from this component.
 */
const TextDropdownMultiRhf = <T extends ListItem>(
  props: Readonly<TextDropdownMultiRhfProps<T>>,
  ref?: ForwardedRef<SelectRefCallback>
) => {
  const internalRef = useRef<SelectRefCallback>(null);
  const actualRef = ref ? (ref as RefObject<SelectRefCallback>) : internalRef;

  const { name, rules, isDropdownMulti = true } = props;
  const { control, setValue, unregister } = useFormContext();
  const fieldValue: TKey<T> = useWatch({ control, name });

  // Event that handles the "natural" flow of data change and
  // synchronizes the selected keys with the field value from RHF.
  // data flow: react-aria -> react-hook-form
  const onSelectionChange = useCallback(
    (keys: Key | Key[]) => {
      // Prevent infinite loops when synchronizing states as
      // multiple re-renders will occur.
      if (isEqual(fieldValue, keys)) return;
      setValue(name, keys, { shouldDirty: true, shouldValidate: true });
    },
    [fieldValue, name, setValue]
  );

  // This effect is used to synchronize the selected keys
  // with the field value when form fields are set programmatically.
  // data flow: react-hook-form -> react-aria
  useEffect(() => {
    if (!actualRef.current) return;

    const { selectedKeys, clearSelection, setSelectedKeys } = actualRef.current;
    if (isFieldValueEqual(fieldValue, selectedKeys)) return;
    if (fieldValue == null) {
      clearSelection();
      return;
    }

    // What we really care is the correct type in react-hook-form.
    // react-aria has some type issues when creating the collection and it
    // auto coerces the keys into strings. Hence, from this point on,
    // we will treat all keys as strings. The only location where it should
    // potentially coerce into numbers is on the `onSelectionChange` event,
    // which is responsible for synch between react-aria and react-hook-form.
    const selected = reactAriafyValue(fieldValue);
    setSelectedKeys(selected);
  }, [actualRef, fieldValue, isDropdownMulti, name]);

  // This handles the unmounting of the component and unregisters
  // the field from react-hook-form if the `shouldUnregister` rule is set.
  useEffect(() => {
    return () => {
      if (props.rules?.shouldUnregister) {
        unregister(name);
      }
    };
  }, [name, props.rules?.shouldUnregister, unregister]);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const multiProps = {
          ...props,
          value: field.value,
          invalid: fieldState.invalid,
          error: fieldState.error?.message,
        };

        return (
          <BaseTextDropdownMulti
            {...multiProps}
            ref={actualRef}
            onSelectionChange={onSelectionChange}
          />
        );
      }}
    />
  );
};

export const TextDropdownMultiV2 = forwardRef(TextDropdownMultiRhf) as <
  T extends ListItem
>(
  props: Readonly<TextDropdownMultiRhfProps<T>> & { ref?: Ref<SelectRefCallback> }
) => ReturnType<typeof TextDropdownMultiRhf>;
